import React from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './Proposals.css';

// Components
import ProposalsHeader from '../components/ProposalsHeader';
import StatsGrid from '../components/StatsGrid';
import ProposalsFilterBar from '../components/ProposalsFilterBar';
import ProposalsGrid from '../components/ProposalsGrid';
import DeleteRFPModal from '../components/Modals/DeleteRFPModal';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import ExportModal from '../../rfp/shared/components/ExportModal';
import PPTGenerationModal from '../../ppt/components/PPTGenerationModal';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import DashboardSkeleton from '../components/Skeletons/DashboardSkeleton';

// Hooks & Utils
import { useProposals } from '../hooks/useProposals';
import { ITEMS_PER_PAGE } from '../constants/proposalUtils';

const Proposals = ({ user, globalSearch }) => {
    const {
        rfpStats, metrics, loading, error,
        searchQuery, setSearchQuery, statusFilter, setStatusFilter,
        currentPage, selectedIds,
        showExport, setShowExport, selectedRfpId,
        itemToDelete, setItemToDelete,
        isDeleting, successToast, isFetchingFullData, fetchingRfpTitle,
        isBulkDownloading, showMoreFilters, setShowMoreFilters,
        moreFiltersRef, handleToggleSelect, handleActionClick,
        confirmDelete, handleBulkDownload,
        filteredResults, totalPages, paginatedData, handlePageChange,
        isNextDisabled, metadata, isFilterChanging, isManager
    } = useProposals(globalSearch, user);
    const navigate = useNavigate();
    const [showPPTModal, setShowPPTModal] = React.useState(false);

    if (loading) return <DashboardSkeleton isManager={isManager} />;

    const totalCount = isManager ? (metadata?.total_count || 0) : filteredResults.length;
    const startRange = totalCount === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endRange = Math.min(totalCount, currentPage * ITEMS_PER_PAGE);

    return (
        <>
            <div className="proposals-page">
                <ProposalsHeader
                    selectedCount={selectedIds.length}
                    onDeleteBulk={() => setItemToDelete({
                        proposal_title: `${selectedIds.length} selected proposals`,
                        filename: 'selected items',
                        multiple: true
                    })}
                    onDownloadBulk={handleBulkDownload}
                    isBulkDownloading={isBulkDownloading}
                />

                {error && <div className="login-error-message" style={{ margin: '0 0 2rem 0' }}>{error}</div>}

                <StatsGrid metrics={metrics} rfpData={paginatedData} />

                <div className="table-container-main">
                    <ProposalsFilterBar
                        searchQuery={searchQuery}
                        onSearchChange={(val) => {
                            setSearchQuery(val);
                            handlePageChange(1);
                        }}
                        statusFilter={statusFilter}
                        onStatusChange={(key) => {
                            setStatusFilter(key);
                            handlePageChange(1);
                        }}
                        rfpStats={rfpStats}
                        showMoreFilters={showMoreFilters}
                        setShowMoreFilters={setShowMoreFilters}
                        moreFiltersRef={moreFiltersRef}
                        selectedCount={selectedIds.length}
                        onDownloadBulk={handleBulkDownload}
                        onDeleteBulk={() => setItemToDelete({
                            proposal_title: `${selectedIds.length} selected proposals`,
                            filename: 'selected items',
                            multiple: true
                        })}
                        isBulkDownloading={isBulkDownloading}
                    />

                    {isFilterChanging ? (
                        <div className="filter-loader-container">
                            <CustomLoader className="loader-spin" size={40} />
                            <p>Updating proposals...</p>
                        </div>
                    ) : (
                        <ProposalsGrid
                            data={paginatedData}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                            onActionClick={handleActionClick}
                        />
                    )}

                    <div className="table-footer">
                        <span className="footer-info">
                            Showing {startRange} to {endRange} out of {totalCount} proposals
                        </span>
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    className="page-nav"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`page-num ${currentPage === i + 1 ? 'active' : ''}`}
                                        onClick={() => handlePageChange(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    className="page-nav"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={isNextDisabled}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Overlays & Modals */}
            {isFetchingFullData && (
                <div className="fetching-overlay">
                    <div className="fetching-content">
                        <CustomLoader className="loader-spin" size={48} />
                        <div className="fetching-text">
                            <h3>Opening Proposal</h3>
                            <p>Fetching full details for <strong>{fetchingRfpTitle}</strong>...</p>
                        </div>
                    </div>
                </div>
            )}

            <DeleteRFPModal
                item={itemToDelete}
                isDeleting={isDeleting}
                onConfirm={confirmDelete}
                onCancel={() => setItemToDelete(null)}
            />

            <SuccessToast toast={successToast} />

            {(() => {
                const selectedRfpData = paginatedData.find(item => String(item._id || item.rfp_id || item.id) === String(selectedRfpId));
                return (
                    <>
                        {showExport && (
                            <ExportModal
                                isOpen={showExport}
                                onClose={() => setShowExport(false)}
                                rfpId={selectedRfpId}
                                analysisData={selectedRfpData}
                                onPPTClick={() => {
                                    setShowExport(false);
                                    setShowPPTModal(true);
                                }}
                            />
                        )}
                        <PPTGenerationModal
                            isOpen={showPPTModal}
                            onClose={() => setShowPPTModal(false)}
                            rfpId={selectedRfpId}
                            navigate={navigate}
                            editableData={selectedRfpData}
                        />
                    </>
                );
            })()}

            {isBulkDownloading && (
                <div className="fetching-overlay" style={{ zIndex: 10000 }}>
                    <div className="fetching-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2.5rem', borderRadius: '12px', minWidth: '320px', background: 'white', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CustomLoader className="loader-spin" size={60} />
                            <div style={{ position: 'absolute', color: '#FF5526', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Download size={22} />
                            </div>
                        </div>
                        <div className="fetching-text" style={{ textAlign: 'center', margin: 0 }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1E293B', marginBottom: '4px' }}>Downloading PDF</h3>
                            <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>Please wait while we bundle your proposals...</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Proposals;
